import { nextTick } from 'vue'
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth.store'
import { keycloakService } from '@/services/keycloak.service'
import { resetCarregamento } from '@/plugins/loading'

declare module 'vue-router' {
  interface RouteMeta {
    permissao?: string
    /** Abre a tela em modo Transcrição (mesmo formulário, outro ponto de entrada). */
    transcricao?: boolean
  }
}

const routes: RouteRecordRaw[] = [
  { path: '/', redirect: { name: 'Avisos' } },

  { path: '/invalid-user', name: 'InvalidUser', component: () => import('../views/invalid-user/invalid-user.vue') },
  { path: '/acesso-negado', name: 'AcessoNegado', component: () => import('../views/access/access.vue') },
  { path: '/login', name: 'Login', component: () => import('../views/PlaceholderView.vue') },
  // Tela de convite aposentada com o desligamento do legado: links antigos caem na Home.
  { path: '/experimente-novo', redirect: '/' },

  {
    path: '/protegido/beneficiario/imp/verifica-elegibilidade',
    name: 'VerificaElegibilidade',
    component: () => import('../views/verifica-elegibilidade/verifica-elegibilidade.vue'),
    meta: { permissao: 'MPScConecteBeneficiario_VerificaElegibilidade' },
  },
  {
    path: '/protegido/autorizacao/imp/busca-solicitacoes-online',
    name: 'BuscaSolicitacoesOnline',
    component: () => import('../views/busca-solicitacoes-online/busca-solicitacoes-online.vue'),
    meta: { permissao: 'MPScConecteBuscaSolicitacoesOnline' },
  },
  {
    path: '/protegido/prevencao/imp/cadastro-prontuario',
    name: 'CadastroProntuario',
    component: () => import('../views/cadastro-prontuario/cadastro-prontuario.vue'),
    meta: { permissao: 'MPScConecteProntuario' },
  },
  {
    path: '/protegido/autorizacao/imp/atendimento-spsadt',
    name: 'AtendimentoSpsadt',
    component: () => import('../views/atendimento-spsadt/atendimento-spsadt.vue'),
    meta: { permissao: 'MPScConecte-atendimento-SolicitaProcedimento' },
  },
  /**
   * Transcrição — lançamento manual de guias que chegaram por outro canal (papel, telefone).
   *
   * Reusa DE PROPÓSITO as mesmas telas de solicitação: a regra de negócio, os campos, as
   * validações e o envio ao TISS são idênticos aos de "Solicitar Autorização" e "Solicitar
   * Internação de RN". O que muda é o ponto de entrada, a permissão e o título — por isso são
   * rotas novas apontando para os mesmos componentes, e não cópias deles. Duplicar 2.500 linhas
   * criaria duas versões que divergem no primeiro ajuste.
   *
   * `meta.transcricao` é o que a tela lê para trocar o título.
   */
  {
    path: '/protegido/autorizacao/imp/transcricao-spsadt',
    name: 'TranscricaoSpsadt',
    component: () => import('../views/atendimento-spsadt/atendimento-spsadt.vue'),
    meta: { permissao: 'MPScConecte-atendimento-TranscricaoSpsadt', transcricao: true },
  },
  {
    path: '/protegido/autorizacao/imp/transcricao-internacao',
    name: 'TranscricaoInternacao',
    component: () => import('../views/atendimento-internacao/atendimento-internacao.vue'),
    meta: { permissao: 'MPScConecte-atendimento-TranscricaoInternacao', transcricao: true },
  },
  {
    path: '/protegido/autorizacao/imp/atendimento-solicitacao-online',
    name: 'AtendimentoSolicitacaoOnline',
    component: () => import('../views/atendimento-solicitacao-online/atendimento-solicitacao-online.vue'),
    meta: { permissao: 'MPScConecte-atendimento-ExecutaProcedimento' },
  },
  {
    path: '/protegido/autorizacao/imp/atendimento-tratamento-seriado',
    name: 'AtendimentoTratamentoSeriado',
    component: () => import('../views/atendimento-tratamento-seriado/atendimento-tratamento-seriado.vue'),
    meta: { permissao: 'MPScConecte-atendimento-ExecutaTratamentoSeriado' },
  },
  /**
   * Os três anexos clínicos seguem a mesma regra, pelo mesmo motivo do OPME logo abaixo: são
   * passos dentro de um fluxo já autorizado, não destinos de menu, e no legado nenhuma das três
   * páginas tem uma única verificação de permissão.
   *
   * Exigir SolicitaProcedimento aqui barrava a Transcrição SP/SADT: ela reusa a tela de SP/SADT,
   * então quem tivesse apenas MPScConecte-atendimento-TranscricaoSpsadt clicava em Anexos e caía
   * em AcessoNegado.
   */
  {
    path: '/protegido/autorizacao/imp/anexo-clinico-quimioterapia',
    name: 'AnexoClinicoQuimioterapia',
    component: () => import('../views/anexo-clinico-quimioterapia/anexo-clinico-quimioterapia.vue'),
  },
  {
    path: '/protegido/autorizacao/imp/anexo-clinico-radioterapia',
    name: 'AnexoClinicoRadioterapia',
    component: () => import('../views/anexo-clinico-radioterapia/anexo-clinico-radioterapia.vue'),
  },
  /**
   * Página cheia, como no legado, e com TRÊS origens: Solicitar Autorização (SP/SADT), Solicitar
   * Internação de RN e Solicitação de Eventos de Internação. No legado é uma URL só, para a qual
   * MbBoSolicitarSpSadt, MbBoSolicitarInternacao e MbBoSolicitacaoEventos redirecionam.
   *
   * Sem `permissao` de propósito. O anexo não é destino de menu: é um passo dentro de um fluxo que
   * já foi autorizado na tela de origem, e o legado não tem verificação de permissão nenhuma nesta
   * página. Exigir uma aqui obrigaria a escolher entre SolicitaProcedimento e SolicitaInternacao —
   * e qualquer das duas nega o acesso a metade dos chamadores, além de barrar quem tenha só as
   * permissões de Transcrição. O `beforeEach` continua exigindo sessão e claims válidos, e a tela
   * sem o rascunho na store abre vazia e não envia nada.
   */
  {
    path: '/protegido/autorizacao/imp/anexo-clinico-opme',
    name: 'AnexoClinicoOpme',
    component: () => import('../views/anexo-clinico-opme/anexo-clinico-opme.vue'),
  },
  {
    path: '/protegido/autorizacao/imp/formulario-fisioterapia/:idAtendimento',
    name: 'FormularioFisioterapia',
    component: () => import('../views/formulario-fisioterapia/formulario-fisioterapia.vue'),
    meta: { permissao: 'MPScConecte-atendimento-ExecutaTratamentoSeriado' },
  },
  {
    path: '/protegido/autorizacao/imp/atendimento-internacao',
    name: 'AtendimentoInternacao',
    component: () => import('../views/atendimento-internacao/atendimento-internacao.vue'),
    meta: { permissao: 'MPScConecte-atendimento-SolicitaInternacao' },
  },
  {
    path: '/protegido/autorizacao/imp/busca-atendimento',
    name: 'BuscaAtendimento',
    component: () => import('../views/busca-atendimento/busca-atendimento.vue'),
    meta: { permissao: 'MPScConectePesquisarAtendimento' },
  },
  {
    path: '/protegido/autorizacao/imp/busca-internacao',
    name: 'BuscaInternacao',
    component: () => import('../views/busca-internacao/busca-internacao.vue'),
    meta: { permissao: 'MPScConectePesquisarInternacao' },
  },
  {
    path: '/protegido/autorizacao/imp/busca-atendimento-solicitados',
    name: 'BuscaAtendimentoSolicitados',
    component: () => import('../views/busca-atendimento-solicitados/busca-atendimento-solicitados.vue'),
    meta: { permissao: 'MPScConecteAtendimentosSolicitados' },
  },
  {
    path: '/protegido/autorizacao/imp/detalha-atendimento/:id',
    name: 'DetalheAtendimento',
    component: () => import('../views/detalha-atendimento/detalha-atendimento.vue'),
    meta: { permissao: 'MPScConectePesquisarAtendimento' },
  },
  {
    // :id opcional: normalmente a tela é aberta a partir de Detalhar Atendimento (dados via
    // store); com o id na URL, a tela também se inicializa sozinha (acesso direto/refresh).
    path: '/protegido/autorizacao/imp/solicitacao-eventos/:id?',
    name: 'SolicitacaoEventos',
    component: () => import('../views/i-solicitacao-eventos/i-solicitacao-eventos.vue'),
    meta: { permissao: 'MPScConectePesquisarAtendimento' },
  },
  {
    path: '/protegido/autorizacao/imp/solicitacao-evento-internacao',
    name: 'SolicitacaoEventoInternacao',
    component: () => import('../views/solicitacao-evento-internacao/solicitacao-evento-internacao.vue'),
    // Aberta a partir de Detalhar Atendimento; a habilitação do botão já valida a regra de acesso.
    meta: { permissao: 'MPScConecte-atendimento-SolicitaInternacao' },
  },
  {
    path: '/protegido/autorizacao/imp/detalha-atendimento-internacao/:id',
    name: 'DetalheInternacao',
    component: () => import('../views/detalha-atendimento-internacao/detalha-atendimento-internacao.vue'),
    meta: { permissao: 'MPScConectePesquisarInternacao' },
  },
  {
    path: '/protegido/autorizacao/imp/busca-justificativas',
    name: 'BuscaJustificativas',
    component: () => import('../views/busca-justificativas/busca-justificativas.vue'),
    meta: { permissao: 'MPScConecteAguardandoJustificativa' },
  },
  {
    // Os dois handles vêm na URL porque no legado eles viviam na sessão do JSF, e um F5 na tela
    // de justificar deixava tudo em branco. Na rota, a tela se reconstrói sozinha.
    path: '/protegido/autorizacao/imp/justifica-atendimento-item/:handleAutorizacao/:handleSolicitacao',
    name: 'JustificaAtendimentoItem',
    component: () => import('../views/justifica-atendimento-item/justifica-atendimento-item.vue'),
    meta: { permissao: 'MPScConecteAguardandoJustificativa' },
  },
  {
    path: '/protegido/autorizacao/imp/beneficiario-internado',
    name: 'BeneficiarioInternado',
    component: () => import('../views/beneficiario-internado/beneficiario-internado.vue'),
    meta: { permissao: 'MPScConecteBeneficiarioInternado' },
  },
  {
    path: '/protegido/autorizacao/imp/atendimento-resumo-mensal',
    name: 'AtendimentoResumoMensal',
    component: () => import('../views/atendimento-resumo-mensal/atendimento-resumo-mensal.vue'),
  },
  {
    path: '/protegido/faturamento/imp/busca-guias-faturar',
    name: 'BuscaGuiasFaturar',
    component: () => import('../views/busca-guias-faturar/busca-guias-faturar.vue'),
    meta: { permissao: 'MPScConecteConsultaGuiasFaturar' },
  },
  {
    path: '/protegido/pesquisa/pesquisa-geral',
    name: 'PesquisaSatisfacao',
    component: () => import('../views/pesquisa-satisfacao/pesquisa-satisfacao.vue'),
  },
  {
    path: '/protegido/faturamento/imp/busca-lotes-faturar',
    name: 'BuscaLotesFaturar',
    component: () => import('../views/busca-lotes-faturar/busca-lotes-faturar.vue'),
    meta: { permissao: 'MPScConecteConsultaLote' },
  },
  {
    path: '/protegido/faturamento/imp/detalha-guia-faturar',
    name: 'DetalhaGuiaFaturar',
    component: () => import('../views/detalha-guia-faturar/detalha-guia-faturar.vue'),
    meta: { permissao: 'MPScConecteConsultaGuiasFaturar' },
  },
  /*
   * Regularização das guias que ficaram sem lote. A permissão aqui só esconde a rota — quem
   * protege de verdade é o backend (RegularizacaoDeLoteService), porque `temPermissao` devolve
   * true quando a lista de permissões vem vazia em `npm run dev`.
   */
  {
    path: '/protegido/faturamento/imp/guias-sem-lote',
    name: 'GuiasSemLote',
    component: () => import('../views/guias-sem-lote/guias-sem-lote.vue'),
    meta: { permissao: 'MPScConecteAssociarGuiasSemLote' },
  },
  {
    path: '/protegido/prestador/imp/cria-eventos-favoritos',
    name: 'CriaEventosFavoritos',
    component: () => import('../views/cria-eventos-favoritos/cria-eventos-favoritos.vue'),
    meta: { permissao: 'MPScConecteCriarListaEventosFavoritos' },
  },
  {
    path: '/protegido/prestador/imp/listas-eventos-favoritos',
    name: 'ListasEventosFavoritos',
    component: () => import('../views/listas-eventos-favoritos/listas-eventos-favoritos.vue'),
    meta: { permissao: 'MPScConecteListarEventosFavoritos' },
  },
  {
    path: '/protegido/faturamento/imp/lotes-por-executante',
    name: 'LotesPorExecutante',
    component: () => import('../views/lotes-por-executante/lotes-por-executante.vue'),
    meta: { permissao: 'MPScConecteAcompanhamentoLotePorExec' },
  },
  {
    path: '/protegido/avisos/imp/cadastro-avisos',
    name: 'CadastroAvisos',
    component: () => import('../views/cadastro-avisos/cadastro-avisos.vue'),
    meta: { permissao: 'MPScConecteCadastroDeAvisos' },
  },
  {
    path: '/protegido/avisos/imp/avisos',
    name: 'Avisos',
    component: () => import('../views/avisos/avisos.vue'),
    meta: { permissao: 'MPScConecteListagemDeAvisos' },
  },

  {
    path: '/protegido/admin',
    name: 'Admin',
    component: () => import('../views/admin/admin.vue'),
    meta: { permissao: 'MPScConecteAdministrador' },
  },

  { path: '/:pathMatch(.*)*', name: 'NotFound', component: () => import('../views/PlaceholderView.vue') },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  /**
   * Quem rola não é a janela: o `.layout-wrapper` é `overflow: hidden` com 100vh e a rolagem
   * vive no `.layout-main`. Mandar a window para o topo não fazia nada — por isso quem clicava
   * em Anexos no rodapé da guia caía no fim do formulário do anexo.
   *
   * O nextTick é necessário porque a tela de destino ainda não foi montada quando isto roda.
   */
  async scrollBehavior(_to, _from, savedPosition) {
    await nextTick()

    const conteudo = document.querySelector<HTMLElement>('.layout-main')
    if (conteudo) conteudo.scrollTop = savedPosition?.top ?? 0

    return savedPosition ?? { top: 0 }
  },
})

const rotasPublicas = new Set<string | symbol>(['InvalidUser', 'AcessoNegado', 'Login', 'NotFound'])

router.beforeEach(async (to) => {
  // Zera o overlay de carregamento herdado da página anterior antes de montar a nova.
  // Sem isso, chamadas HTTP que ficam em voo depois da navegação mantêm pendentes > 0 e o
  // LoadingOverlay cobre a nova página até o timeout de 250 s — tela "em branco" para o usuário.
  resetCarregamento()

  if (rotasPublicas.has(to.name ?? '')) return true

  // Valida claims obrigatórios no token Keycloak
  const token = keycloakService.keycloak.tokenParsed as any
  if (!token?.codigo_credenciado || !token?.given_name) {
    return { name: 'InvalidUser' }
  }

  // Carrega permissões uma única vez (idempotente)
  const authStore = useAuthStore()
  await authStore.carregarPermissoes()

  // Verifica permissão específica da rota
  if (to.meta.permissao && !authStore.temPermissao(to.meta.permissao)) {
    return { name: 'AcessoNegado' }
  }

  return true
})

export default router
